import C from "@/modules/main/TopNav.module.css";

import { WToolButton } from "@/components/button/WToolButton.tsx";
import { Toolbar } from "@/components/Toolbar.tsx";
import { ToolbarText } from "@/components/ToolbarText.tsx";
import { currentApiParamsAtom } from "@/modules/api/apiClient.store.ts";
import { getWebsocketUrl, type ApiParameters } from "@/modules/api/createApiClient.ts";
import type { VirtualMachineWithAssigned } from "@/modules/compute/joinAssignedQuery.ts";
import { useTitle } from "@/utils/react/useTitle.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import type { NoVncCredentials } from "@novnc/novnc/lib/rfb";
import { Link } from "@tanstack/react-router";
import type { AdminVirtualMachine } from "@warrenio/api-spec/spec.oats.gen";
import { useStore } from "jotai/react";
import { useMemo, useRef, useState, type ReactNode } from "react";
import { VncScreen, type VncScreenHandle } from "react-vnc";
import invariant from "tiny-invariant";

interface VncWsUrl extends ApiParameters {
    uuid: string;
    location: string;
    isAdmin: boolean;
}

function getVncWsUrl({ uuid, location, isAdmin, apikey, apiPrefix }: VncWsUrl) {
    invariant(apikey, "API key must be set");
    const search = new URLSearchParams({
        apikey,
        token: `${apikey}:${uuid}${isAdmin ? ":admin" : ""}`,
    });
    return `${getWebsocketUrl(apiPrefix)}/${location}/vm-console?${search}`;
}

function getVncPassword(uuid: string) {
    return uuid.split("").reverse().join("");
}

type ConnectionState = "connecting" | "connected" | "disconnected";

/**
 * Documentation:
 * - https://github.com/roerohan/react-vnc
 *      - Source code: https://github.com/roerohan/react-vnc/blob/main/src/lib/VncScreen.tsx
 * - https://github.com/novnc/noVNC/blob/master/docs/API.md
 */
export function WVnc({
    vm,
    location,
    isAdmin = false,
    toolbar,
}: {
    vm: VirtualMachineWithAssigned | AdminVirtualMachine;
    location: string;
    isAdmin?: boolean;
    toolbar?: ReactNode;
}) {
    const { t } = useLingui();
    //#region Hooks
    const screen = useRef<VncScreenHandle>(null);
    const store = useStore();
    const apiParams = useMemo(() => store.get(currentApiParamsAtom), [store]);
    const [state, setState] = useState<ConnectionState>("connecting");

    const { uuid, name } = vm;

    const title = `${name} | ${t`Terminal`}`;
    useTitle(title);
    //#endregion

    const vncUrl = getVncWsUrl({ uuid, location, isAdmin, ...apiParams });

    // TODO: Resize remote screen to fit
    return (
        <div className="flex flex-col h-full">
            <div className="flex justify-between items-center">
                <Toolbar className="flex-1">
                    <ToolbarText icon="jp-monitor-icon size-0.875rem color-primary">{title}</ToolbarText>

                    <WToolButton
                        icon="jp-media-loop-icon"
                        label={t`Send Ctrl+Alt+Del`}
                        action={() => {
                            screen.current?.sendCtrlAltDel();

                            // Move focus back from button to screen
                            screen.current?.focus();
                        }}
                    />
                    <WToolButton
                        icon="jp-icon-refresh"
                        label={t`Connect`}
                        action={() => {
                            setState("connecting");
                            screen.current?.connect();

                            screen.current?.focus();
                        }}
                        isDisabled={state !== "disconnected"}
                    />
                    {import.meta.env.DEV && (
                        // Allow disconnecting in dev to test loading state

                        <WToolButton
                            icon="i-lucide:bug"
                            label={t`Disconnect`}
                            action={() => {
                                screen.current?.disconnect();

                                screen.current?.focus();
                            }}
                            isDisabled={state === "disconnected"}
                        />
                    )}

                    {toolbar}
                </Toolbar>

                <Link className="pr-4" to="/" aria-label="Home">
                    <div className={`bg-icon ${C.logo}`} />
                </Link>
            </div>

            {/** biome-ignore lint/a11y/noStaticElementInteractions: UX improvement, auto-focus on hover */}
            <div
                // Extra wrapper component so we can position the loading indicator on top
                className="flex-grow flex flex-col relative"
                onMouseEnter={() => {
                    console.debug("Focusing on VNC hover");
                    screen.current?.focus();
                }}
            >
                <LoadingIndicator state={state} />
                <VncScreen
                    ref={screen}
                    // Style
                    // NB: This applies just to the wrapper <div>, not the screen canvas...
                    className="flex-grow bg-#000 size-full"
                    url={vncUrl}
                    // Options
                    autoConnect
                    scaleViewport
                    focusOnClick
                    // Events
                    onConnect={(rfb) => {
                        screen.current!.focus();

                        console.debug("onConnect", rfb);
                        setState("connected");
                    }}
                    onDisconnect={(rfb) => {
                        console.debug("onDisconnect", rfb);
                        setState("disconnected");
                    }}
                    onSecurityFailure={(rfb) => {
                        console.debug("onSecurityFailure", rfb);
                        // TODO: Display errors
                    }}
                    onCredentialsRequired={(rfb) => {
                        console.debug("onCredentialsRequired", rfb);
                        screen.current!.sendCredentials({
                            // NB: Missing `username` etc.
                            password: getVncPassword(uuid),
                        } as NoVncCredentials);
                    }}
                />
            </div>
        </div>
    );
}

function LoadingIndicator({ state }: { state: ConnectionState }) {
    if (state === "connected") {
        return null;
    }

    return (
        <div className="bg-grey-2 text-muted p-2 rounded m-2 absolute left-0 top-0">
            {state === "connecting" ? (
                <>
                    <Trans>Connecting</Trans>...
                </>
            ) : (
                <Trans comment="State of connection: Not connected">Disconnected</Trans>
            )}
        </div>
    );
}
